import { ListLoadError } from '../../components/list-load-error';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  ResponsiveList,
  Skeleton,
  StatusBadge,
  formatDate,
  useToast,
} from '@catering/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { Plus, Search, Wallet } from 'lucide-react';
import { type JSX, useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { formatPriceCents } from '../../contracts/format-price';
import { searchAllCustomers } from '../../customers/api';
import {
  PAYMENT_STATUSES,
  listPayments,
  voidPayment,
  type Payment,
  type PaymentStatus,
} from '../../payments/api';
import { mapPaymentError } from '../../payments/payment-api-errors';
import { PAYMENT_STATUS_LABELS } from '../../payments/payment-labels';
import { VoidPaymentModal } from './components/void-payment-modal';

const paymentsRoute = getRouteApi('/protected/payments');

interface FilterPatch {
  status?: PaymentStatus | undefined;
  customerId?: string | undefined;
  fromDate?: string | undefined;
  toDate?: string | undefined;
}

/**
 * `payment-receipts/spec.md` → "Pantalla de Cobranzas en el back-office".
 * Same skeleton as `InvoiceListPage`: `ActionBar`'s "Registrar recibo"
 * (visible with `payment.register`), `ResponsiveList` filterable by
 * cliente/estado/fecha, row action "Anular" visible only with
 * `payment.void` ("Acción Anular visible solo con el permiso"). Anular
 * itself is inert in this PR: `POST /payments/:id/void` is a later PR's
 * scope, same allowance `design.md` gives `/payments/:id` as a
 * placeholder route for now.
 */
export function PaymentListPage(): JSX.Element {
  const search = paymentsRoute.useSearch();
  const navigate = paymentsRoute.useNavigate();
  const currentUser = useCurrentUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [voidTarget, setVoidTarget] = useState<Payment | undefined>(undefined);

  const canRegister = hasPermission(currentUser.data, 'payment.register');
  const canVoid = hasPermission(currentUser.data, 'payment.void');

  const query = useQuery({
    queryKey: ['payments', search.status, search.customerId, search.fromDate, search.toDate],
    queryFn: () =>
      listPayments({
        status: search.status,
        customerId: search.customerId,
        fromDate: search.fromDate,
        toDate: search.toDate,
        page: 1,
        pageSize: 100,
      }),
    placeholderData: keepPreviousData,
  });

  const customersQuery = useQuery({
    queryKey: ['customers-for-payments'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  const customers = customersQuery.data?.items ?? [];
  const customerNameById = new Map(customers.map((customer) => [customer.id, customer.legalName]));

  function applyFilters(patch: FilterPatch): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  const items = query.data?.items ?? [];
  const hasActiveFilters =
    search.status !== undefined ||
    search.customerId !== undefined ||
    search.fromDate !== undefined ||
    search.toDate !== undefined;

  const voidMutation = useMutation({
    mutationFn: (input: { paymentId: string; reason: string }) =>
      voidPayment(input.paymentId, input.reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
      setVoidTarget(undefined);
      toast('success', 'Recibo anulado');
    },
  });

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Wallet}
        title="Cobranzas"
        description="Consultá los recibos de cobro registrados y su estado."
        actions={
          canRegister ? (
            <Link to="/payments/new">
              <Button icon={Plus}>Registrar recibo</Button>
            </Link>
          ) : undefined
        }
      />

      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Desde</span>
            <input
              type="date"
              value={search.fromDate ?? ''}
              onChange={(event) =>
                applyFilters({ fromDate: event.target.value === '' ? undefined : event.target.value })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Hasta</span>
            <input
              type="date"
              value={search.toDate ?? ''}
              onChange={(event) =>
                applyFilters({ toDate: event.target.value === '' ? undefined : event.target.value })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Cliente</span>
            <select
              value={search.customerId ?? ''}
              onChange={(event) =>
                applyFilters({ customerId: event.target.value === '' ? undefined : event.target.value })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.legalName}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Estado</span>
            <select
              value={search.status ?? ''}
              onChange={(event) =>
                applyFilters({
                  status: event.target.value === '' ? undefined : (event.target.value as PaymentStatus),
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos</option>
              {PAYMENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {PAYMENT_STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      {query.isError ? (
        <ListLoadError
          title="No pudimos cargar los recibos"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton data-testid="payments-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="payments-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="payments-skeleton-row" className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && items.length === 0 ? (
        <EmptyState
          icon={hasActiveFilters ? Search : Wallet}
          title={hasActiveFilters ? 'No se encontraron recibos.' : 'Todavía no hay recibos registrados.'}
        />
      ) : null}

      {query.data !== undefined && query.data.total > items.length ? (
        <Alert tone="warning">
          Mostrando {items.length} de {query.data.total} recibos. Ajustá los filtros para ver el
          resto.
        </Alert>
      ) : null}

      {query.data !== undefined && items.length > 0 ? (
        <ResponsiveList
          items={items}
          rowKey={(payment) => payment.id}
          caption="Recibos"
          getRowTestId={(payment) => `payment-card-${payment.id}`}
          emptyState={null}
          {...(canVoid
            ? {
                renderActions: (payment: Payment) =>
                  payment.status === 'VOIDED' ? null : (
                    <Button variant="secondary" size="sm" onClick={() => {
                        voidMutation.reset();
                        setVoidTarget(payment);
                      }}>
                      Anular
                    </Button>
                  ),
              }
            : {})}
          columns={[
            {
              key: 'receiptNumber',
              header: 'Número',
              render: (payment) => (
                <Link
                  to="/payments/$id"
                  params={{ id: payment.id }}
                  className="font-medium text-slate-900 hover:underline"
                >
                  {payment.receiptNumber}
                </Link>
              ),
            },
            {
              key: 'customer',
              header: 'Cliente',
              render: (payment) => customerNameById.get(payment.customerId) ?? '—',
            },
            { key: 'date', header: 'Fecha', render: (payment) => formatDate(payment.paymentDate) },
            {
              key: 'amount',
              header: 'Monto',
              render: (payment) => formatPriceCents(payment.amountReceivedCents),
            },
            {
              key: 'method',
              header: 'Método',
              render: (payment) => payment.method.name,
            },
            {
              key: 'status',
              header: 'Estado',
              render: (payment) => <StatusBadge domain="payment" status={payment.status} />,
            },
          ]}
          renderCardHeader={(payment) => (
            <Link
              to="/payments/$id"
              params={{ id: payment.id }}
              className="flex items-center justify-between gap-2"
            >
              <span>{payment.receiptNumber}</span>
              <StatusBadge domain="payment" status={payment.status} />
            </Link>
          )}
          renderCardBody={(payment) => (
            <Link
              to="/payments/$id"
              params={{ id: payment.id }}
              className="col-span-2 grid grid-cols-2 gap-2"
            >
              <span>{customerNameById.get(payment.customerId) ?? '—'}</span>
              <span>{formatPriceCents(payment.amountReceivedCents)}</span>
              <span className="col-span-2">{formatDate(payment.paymentDate)}</span>
            </Link>
          )}
        />
      ) : null}

      {voidTarget !== undefined ? (
        <VoidPaymentModal
          submitting={voidMutation.isPending}
          error={voidMutation.isError ? mapPaymentError(voidMutation.error) : undefined}
          onCancel={() => setVoidTarget(undefined)}
          onConfirm={(reason) => voidMutation.mutate({ paymentId: voidTarget.id, reason })}
        />
      ) : null}
    </section>
  );
}
